import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "影视资讯", to: "/category/news" },
  { label: "剧情解析", to: "/category/plot" },
  { label: "作品库", to: "/category/works" },
  { label: "关于", to: "/about" },
  { label: "联系", to: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-label text-lg font-semibold">
            黑
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading font-black text-lg tracking-tight ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              黑皮辣妹
            </span>
            <span
              className={`font-label text-[10px] tracking-[0.25em] uppercase ${
                transparent ? "text-background-200" : "text-foreground-500"
              }`}
            >
              Film Channel
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(item.to)
                  ? transparent
                    ? "text-background-50 bg-foreground-950/20"
                    : "text-primary-600 bg-primary-50"
                  : transparent
                    ? "text-background-100 hover:text-background-50 hover:bg-foreground-950/20"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={submitSearch} className="hidden md:flex items-center">
            <div
              className={`flex items-center rounded-md border overflow-hidden ${
                transparent
                  ? "bg-foreground-950/30 border-background-100/30"
                  : "bg-background-100 border-background-200"
              }`}
            >
              <i
                className={`ri-search-line pl-3 text-base ${
                  transparent ? "text-background-100" : "text-foreground-500"
                }`}
              />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视内容"
                className={`w-32 xl:w-44 bg-transparent text-sm px-2 py-2 outline-none ${
                  transparent
                    ? "text-background-50 placeholder:text-background-200"
                    : "text-foreground-900 placeholder:text-foreground-400"
                }`}
              />
              <button
                type="submit"
                className="bg-primary-500 text-background-50 text-sm px-3.5 py-2 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>

          <Link
            to="/category/works"
            className="hidden md:inline-flex items-center gap-1.5 bg-accent-500 text-background-50 text-sm font-medium px-4 py-2 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-fire-line" />
            热门榜单
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
              transparent ? "text-background-50" : "text-foreground-900"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} className="flex items-center">
            <div className="flex items-center flex-1 rounded-md border border-background-200 bg-background-100 overflow-hidden">
              <i className="ri-search-line pl-3 text-base text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视内容"
                className="flex-1 bg-transparent text-sm px-2 py-2.5 outline-none text-foreground-900 placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="bg-primary-500 text-background-50 text-sm px-4 py-2.5 whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-3 rounded-md text-sm font-medium ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-600"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <Link
            to="/category/works"
            className="flex items-center justify-center gap-1.5 bg-accent-500 text-background-50 text-sm font-medium px-4 py-3 rounded-md"
          >
            <i className="ri-fire-line" />
            热门榜单
          </Link>
        </div>
      )}
    </header>
  );
}